import { useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { Box, useTheme } from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";

import FormTextInput from "../FormTextInput";
import ApiError from "../../api/common/apiError";
import FormErrors from "../FormErrors";
import ConfirmDialog from "../ConfirmDialogBox";
import { useAppPaths } from "../../Routes";
import useConfirmDialogBoxState from "../ConfirmDialogBox/useConfirmDialogBoxState";
import useIsLargeScreen from "../../hooks/useIsLargeScreen";
import FormCard from "../FormCard";
import { GigApiFetcherResponse } from "../../api/common/fetching";
import { SkillCreateFormValues, useSkillsForm } from "./SkillCreateFormValues";
import FormInputLabel from "../FormInputLabel";
import { SkillCategoryInput } from "../CategoriesSearchDropdown";

type SkillCreateFormProps = {
    onSubmit: (values: SkillCreateFormValues) => Promise<GigApiFetcherResponse<string>>
    isSubmitting: boolean
}

const SkillCreateForm = ({
    onSubmit,
    isSubmitting
}: SkillCreateFormProps) => {
    const appPaths = useAppPaths();
    const navigate = useNavigate();
    const theme = useTheme();
    const isLargeScreen = useIsLargeScreen();
    const [submissionError, setSubmissionError] = useState<ApiError | undefined>();
    const [openCancelDialog, cancelDialogState] = useConfirmDialogBoxState({
        onConfirm: () => {
            navigate(appPaths.skills.categories.index);
        }
    });

    const methods = useSkillsForm();

    const {
        formState: { isDirty, errors }
    } = methods;

    const handleSubmit = async (values: SkillCreateFormValues) => {
        setSubmissionError(undefined);

        const response = await onSubmit(values);

        if (!response.success) setSubmissionError(response.error);
    };

    const handleCloseButton = () => {
        if (isDirty) {
            openCancelDialog();
        } else {
            navigate(appPaths.skills.index);
        }
    };

    return (
        <>
            <FormProvider {...methods}>
                <form
                    onSubmit={methods.handleSubmit(handleSubmit)}
                    noValidate
                >
                    <FormCard onClose={handleCloseButton} title="Create Insights Workshop Type">
                        <Box sx={{
                            "> *:not(:last-child)": {
                                marginBottom: theme.spacing(2),
                            }
                        }}>
                            <FormTextInput name="skillName" label="Insights workshop type name" disabled={isSubmitting} required />
                            <FormErrors messages={submissionError?.userMessages} />
                            <Box className="space-y-2 flex flex-col">
                                <>
                                    <FormInputLabel>Insights workshop type category</FormInputLabel>
                                    <Controller
                                        name="skillCategoryId"
                                        control={methods.control}
                                        render={({ field: { onChange, value } }) => (
                                            <SkillCategoryInput
                                                error={errors.skillCategoryId}
                                                onChange={onChange}
                                                value={value}
                                                isSubmitting={isSubmitting}
                                            />
                                        )}
                                    />
                                </>
                            </Box>
                            <Box sx={
                                isLargeScreen ?
                                    { display: "flex", justifyContent: "flex-end" } :
                                    { display: "flex", flexDirection: "column-reverse", justifyContent: "flex-end", marginTop: "2rem" }
                            }>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    startIcon={<SaveOutlinedIcon sx={isSubmitting ? { opacity: "26%" } : { opacity: "100%" }} />}
                                    loading={isSubmitting}
                                    sx={
                                        isLargeScreen ?
                                            { marginBottom: 0 } :
                                            { marginBottom: theme.spacing(2) }
                                    }
                                >
                                    Save Changes
                                </LoadingButton>
                            </Box>
                        </Box>
                    </FormCard>
                </form>
            </FormProvider >
            <ConfirmDialog
                {...cancelDialogState}
                message="Discard unsaved changes?"
                confirmButtonText="Discard"
            />
        </>
    );
};

export default SkillCreateForm;